import { useState, useEffect } from 'react';
import { FileText, AlertCircle, Crown, Calendar } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { usePackAccess } from '../hooks/usePackAccess';

interface DafNote {
  id: string;
  user_id: string;
  month: string;
  admin_notes: string;
  client_notes: string;
  created_at: string;
  updated_at: string;
  created_by_admin: string | null;
}

export function DafSpaceTab() {
  const { canAccessDafSpace, currentPlan } = usePackAccess();
  const [selectedMonth, setSelectedMonth] = useState<string>('');
  const [clientNotes, setClientNotes] = useState('');
  const [loading, setLoading] = useState(false);
  const [currentNote, setCurrentNote] = useState<DafNote | null>(null);

  useEffect(() => {
    const now = new Date();
    const monthStr = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
    setSelectedMonth(monthStr);
  }, []);

  useEffect(() => {
    if (selectedMonth && canAccessDafSpace) {
      loadNote();
    }
  }, [selectedMonth, canAccessDafSpace]);

  const loadNote = async () => {
    if (!selectedMonth) return;

    setLoading(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const { data, error } = await supabase
        .from('daf_notes')
        .select('*')
        .eq('user_id', user.id)
        .eq('month', selectedMonth)
        .maybeSingle();

      if (error && error.code !== 'PGRST116') throw error;

      if (data) {
        setCurrentNote(data);
        setClientNotes(data.client_notes || '');
      } else {
        setCurrentNote(null);
        setClientNotes('');
      }
    } catch (error) {
      console.error('Error loading note:', error);
    } finally {
      setLoading(false);
    }
  };

  const getMonthOptions = () => {
    const options = [];
    const now = new Date();

    for (let i = 0; i < 12; i++) {
      const date = new Date(now.getFullYear(), now.getMonth() - i, 1);
      const value = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
      const label = date.toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' });
      options.push({ value, label });
    }

    return options;
  };

  if (!canAccessDafSpace) {
    return (
      <div className="bg-white rounded-lg shadow-sm p-8">
        <div className="text-center">
          <AlertCircle className="h-12 w-12 text-orange-500 mx-auto mb-4" />
          <h3 className="text-xl font-semibold text-gray-900 mb-2">
            Espace DAF non disponible
          </h3>
          <p className="text-gray-600 mb-6">
            L'espace DAF avec notes internes et compte-rendu mensuel est exclusivement disponible avec le Pack DAF.
            Passez au Pack DAF pour bénéficier d'un accompagnement personnalisé.
          </p>
          <p className="text-sm text-gray-500">
            Pack actuel : <span className="font-semibold">{currentPlan?.name || 'Aucun'}</span>
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="bg-gradient-to-r from-amber-50 to-orange-50 border-l-4 border-amber-600 p-6 rounded">
        <div className="flex items-start">
          <Crown className="h-6 w-6 text-amber-600 mt-0.5 mr-3" />
          <div>
            <h3 className="font-semibold text-amber-900 mb-1">
              Espace DAF - Accompagnement personnalisé
            </h3>
            <p className="text-sm text-amber-800">
              Retrouvez ici les comptes-rendus mensuels de votre accompagnement DAF.
              Votre conseiller DAF y dépose régulièrement des synthèses et recommandations personnalisées.
            </p>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow-sm p-6">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-bold text-gray-900">Compte-rendu mensuel</h2>

          <div className="flex items-center space-x-3">
            <Calendar className="h-5 w-5 text-gray-400" />
            <select
              value={selectedMonth}
              onChange={(e) => setSelectedMonth(e.target.value)}
              className="border border-gray-300 rounded-lg px-4 py-2 text-gray-900 focus:outline-none focus:ring-2 focus:ring-amber-500"
            >
              {getMonthOptions().map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
        </div>

        {loading ? (
          <div className="text-center py-8 text-gray-600">Chargement...</div>
        ) : (
          <div className="space-y-6">
            {clientNotes ? (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <div className="flex items-center">
                    <FileText className="h-4 w-4 mr-2" />
                    Synthèse de votre conseiller DAF
                  </div>
                </label>
                <div className="w-full border border-gray-200 rounded-lg px-4 py-3 bg-gray-50 text-gray-900 min-h-[200px] whitespace-pre-wrap">
                  {clientNotes}
                </div>
              </div>
            ) : (
              <div className="text-center py-12 text-gray-500">
                <FileText className="h-12 w-12 mx-auto mb-3 text-gray-300" />
                <p className="font-medium">Aucun compte-rendu pour ce mois</p>
                <p className="text-sm mt-1">
                  Votre conseiller DAF n'a pas encore déposé de compte-rendu pour cette période.
                </p>
              </div>
            )}

            {currentNote && (
              <div className="text-sm text-gray-500">
                Dernière mise à jour : {new Date(currentNote.updated_at).toLocaleString('fr-FR')}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
